/**
 * DynamicRequestFields
 *
 * Generic, declarative renderer for a request type's extra fields. A request
 * type that only needs simple inputs can be added to the registry as a `fields`
 * array (no new component, no form changes) and this renders it.
 *
 * Field descriptor: { name, label, type, span?, required?, placeholder?, hint?,
 *   options? (select), rows? (textarea), min?/max? (number) }
 * Supported types: text, textarea, number, currency, percent, date, daterange,
 *   url, select, switch.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Input, InputNumber, DatePicker, Select, Switch } from 'antd';
import Text from 'antd/lib/typography/Text';

import { currencyProps, urlValidator, blockNonNumericKeyDown } from './fieldHelpers';
import '../../../styles/index.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

export const renderInput = (field) => {
  switch (field.type) {
    case 'textarea':
      return <TextArea rows={field.rows || 3} placeholder={field.placeholder} className="custom-input" />;
    case 'number':
      return (
        <InputNumber
          min={field.min}
          max={field.max}
          onKeyDown={blockNonNumericKeyDown}
          placeholder={field.placeholder}
          className="custom-input task-w--full"
        />
      );
    case 'currency':
      return <InputNumber {...currencyProps} placeholder={field.placeholder} />;
    case 'percent':
      return (
        <InputNumber
          min={0}
          max={100}
          onKeyDown={blockNonNumericKeyDown}
          formatter={(v) => (v || v === 0 ? `${v}%` : '')}
          parser={(v) => (v ? v.replace(/[^\d.]/g, '') : '')}
          placeholder={field.placeholder}
          className="custom-input task-w--full"
        />
      );
    case 'date':
      return <DatePicker className="custom-date task-w--full" />;
    case 'daterange':
      return <RangePicker className="custom-date task-w--full" />;
    case 'select':
      return (
        <Select
          placeholder={field.placeholder}
          className="custom-select"
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {(field.options || []).map((o) => (
            <Option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </Option>
          ))}
        </Select>
      );
    case 'switch':
      return <Switch />;
    case 'url':
    case 'text':
    default:
      return <Input placeholder={field.placeholder} className="custom-input" />;
  }
};

export const fieldRules = (field) => {
  const rules = [];
  if (field.required) rules.push({ required: true, message: `${field.label} is Required` });
  if (field.type === 'url') rules.push({ validator: urlValidator });
  return rules;
};

const DynamicRequestFields = ({ fields, title }) => (
  <Col className="task-io-fields">
    <Card
      className="task-assignment-fields__card"
      title={<Text className="task-assignment-fields__card-title">{title}</Text>}
    >
      <Row gutter={32}>
        {(fields || []).map((field) => (
          <Col key={field.name} xs={24} sm={24} md={field.span || 12} lg={field.span || 12}>
            <Form.Item
              name={field.name}
              colon={false}
              rules={fieldRules(field)}
              valuePropName={field.type === 'switch' ? 'checked' : undefined}
              label={
                field.hint ? (
                  <div>
                    <div>{field.label}</div>
                    <div className="task-basic-fields__label-hint">{field.hint}</div>
                  </div>
                ) : (
                  field.label
                )
              }
            >
              {renderInput(field)}
            </Form.Item>
          </Col>
        ))}
      </Row>
    </Card>
  </Col>
);

DynamicRequestFields.propTypes = {
  fields: PropTypes.array,
  title: PropTypes.string,
};

DynamicRequestFields.defaultProps = {
  fields: [],
  title: 'REQUEST DETAILS',
};

export default DynamicRequestFields;
